/**
 * System Settings - the business rules the System Administrator maintains.
 * 29-09-2026, migration 0184.
 *
 * WHAT IS HERE
 *   Company-wide rules and Materials rules, all on the one row of
 *   company_profile. Every number starts at the value that was written into
 *   the code before this page existed, so nothing behaved differently on the
 *   day it arrived.
 *
 * WHO
 *   The System Administrator. The database says so too: rpc_set_company_setting
 *   refuses everybody else, and the row level security on company_profile has
 *   allowed only the administrator to write since 0009.
 *
 * EVERY CHANGE IS RECORDED
 *   Save asks for a reason. fn_log_setting_change writes who, when, the old
 *   value, the new value and that reason into document_audit - including
 *   changes made on the older Master Data -> Settings tab, which does not ask
 *   for a reason and is logged as such.
 *
 * WHAT IS NOT HERE
 *   The Service rules (the advance guarantee threshold, the reverse-charge
 *   switch). They belong to the Service module's own table, and the two
 *   modules share no data - so they are on Service Procurement -> Settings,
 *   linked at the top of this page.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, History, Save, SlidersHorizontal } from 'lucide-react'
import {
  Alert, Card, EmptyState, Field, Input, Loading, Modal, Select, Textarea, useToast,
} from '@/components/ui'
import { NotPermitted } from '@/components/docs/shared'
import { useAuth } from '@/lib/auth'
import { usePeople, useQuery } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { dateTime } from '@/lib/format'

type Kind = 'int' | 'pct' | 'bool' | 'qty'

interface SettingDef {
  key: string
  label: string
  group: 'Company-wide' | 'Materials' | 'Materials warnings' | 'Materials starting values'
  kind: Kind
  min?: number
  max?: number
  unit?: string
  /** What it changes, in one line. */
  help: string
  /** How the two answers of a switch read. */
  onLabel?: string
  offLabel?: string
}

/**
 * The rules, in the order they are shown. The ranges here are the ones
 * rpc_set_company_setting and the check constraints in 0184 enforce - this
 * list only says them first.
 */
export const COMPANY_SETTINGS: SettingDef[] = [
  { key: 'backdate_limit_days', label: 'How far back a document may be dated', group: 'Company-wide',
    kind: 'int', min: 1, max: 3650, unit: 'days',
    help: 'Every form, and the database, refuse a document dated earlier than this. Also shown on Master Data → Settings.' },
  { key: 'backdate_within_fy', label: 'Never date a document into the previous financial year', group: 'Company-wide',
    kind: 'bool', onLabel: 'Yes — the previous year is closed', offLabel: 'No — only the day limit above applies',
    help: 'Once a year is closed and audited, nothing should reach back into it, however recent.' },
  { key: 'accounts_grace_days', label: 'Accounts grace after a month is closed', group: 'Company-wide',
    kind: 'int', min: 0, max: 60, unit: 'days',
    help: 'For how many days after closing a month Accounts (and the administrator) may still post into it. Nobody else may, ever.' },
  { key: 'period_close_day', label: 'Day of the month Accounts closes the previous month', group: 'Company-wide',
    kind: 'int', min: 1, max: 28, unit: 'th',
    help: 'For reference only. Closing is still done by Accounts on Accounting Periods; nothing closes itself on this day.' },
  { key: 'block_negative_stock', label: 'Negative stock', group: 'Company-wide',
    kind: 'bool', onLabel: 'Refuse — the site must hold the stock on the date', offLabel: 'Allow, and record every instance',
    help: 'Whether an issue or transfer may take a site below zero on its document date. Also on Master Data → Settings.' },

  { key: 'grn_tolerance_pct', label: 'Over-receipt tolerance on a goods receipt', group: 'Materials',
    kind: 'pct', min: 0, max: 50, unit: '%',
    help: 'How far above the pending purchase-order quantity a receipt may accept before it is flagged to the Head of Stores — and the most a late "Link to a purchase order" may exceed pending.' },
  { key: 'invoice_tolerance_pct', label: 'Invoice tolerance against the goods value', group: 'Materials',
    kind: 'pct', min: 0, max: 50, unit: '%',
    help: 'A vendor bill within this much of the goods value closes billing on its own. Beyond it, the Procurement Manager has to approve the difference in writing.' },
  { key: 'payable_overdue_days', label: 'A payable counts as overdue after', group: 'Materials',
    kind: 'int', min: 1, max: 365, unit: 'days',
    help: 'Drives the "Over N days" cards and the red highlighting on Vendor Payables and on each payable.' },

  // Phase 2, 15-10-2026, migration 0212. Warnings recalculate at once; the
  // starting values apply to NEW forms only - nothing saved is rewritten.
  { key: 'itc_warning_days', label: 'GST credit early warning after', group: 'Materials warnings',
    kind: 'int', min: 1, max: 179, unit: 'days',
    help: 'An unpaid invoice is flagged "Pay within 180 days" after this many days, on Vendor Invoices and the GST credit figures. The 180 days is the law (section 16(2)) and cannot be changed here.' },
  { key: 'stale_rate_days', label: 'A material rate counts as old after', group: 'Materials warnings',
    kind: 'int', min: 1, max: 3650, unit: 'days',
    help: 'On the Rates screen, a last purchase rate older than this is counted as worth re-quoting.' },
  { key: 'vendor_concentration_pct', label: 'Vendor concentration alert at', group: 'Materials warnings',
    kind: 'pct', min: 1, max: 100, unit: '%',
    help: 'Vendor Liabilities warns when the three largest vendors hold at least this share of what the company owes.' },
  { key: 'late_entry_warn_days', label: 'Late entry shown in amber after', group: 'Materials warnings',
    kind: 'int', min: 1, max: 3650, unit: 'days',
    help: 'On Late Entries, a document keyed this long after its own date is highlighted, and counted in the "late" card. Must be below the red limit.' },
  { key: 'late_entry_serious_days', label: 'Late entry shown in red after', group: 'Materials warnings',
    kind: 'int', min: 2, max: 3650, unit: 'days',
    help: 'On Late Entries, a document keyed this long after its own date is shown in red. Must be above the amber limit.' },
  { key: 'po_validity_default_days', label: 'A new purchase order starts with a validity of', group: 'Materials starting values',
    kind: 'int', min: 1, max: 365, unit: 'days',
    help: 'What the validity box holds when a NEW purchase order is opened. The buyer may still change it; saved orders keep their own.' },
  { key: 'material_min_stock_default', label: 'A new material starts with a minimum stock of', group: 'Materials starting values',
    kind: 'qty', min: 0, max: 100000000, unit: '',
    help: 'What the minimum-stock box holds when a NEW material is added on Master Data. Saved materials keep their own.' },
  { key: 'material_max_stock_default', label: 'A new material starts with a maximum stock of', group: 'Materials starting values',
    kind: 'qty', min: 0, max: 100000000, unit: '',
    help: 'What the maximum-stock box holds when a NEW material is added on Master Data. Saved materials keep their own.' },
]

const LABEL: Record<string, string> = Object.fromEntries(COMPANY_SETTINGS.map((s) => [s.key, s.label]))

const show = (d: SettingDef, v: unknown) => {
  if (v === null || v === undefined || v === '') return '—'
  if (d.kind === 'bool') return (v === true || v === 'true') ? 'Yes' : 'No'
  if (d.kind === 'qty') return Number(v).toLocaleString('en-IN')
  return `${Number(v)}${d.unit === '%' ? '%' : d.unit === 'th' ? '' : ` ${d.unit ?? ''}`}`
}

/** What each card says it covers. */
const GROUP_SUBTITLE: Record<SettingDef['group'], string> = {
  'Company-wide': 'Both modules — Materials and Service',
  'Materials': 'Material Procurement and Stores only',
  'Materials warnings': 'When a Materials screen highlights something. A change shows on the screens at once.',
  'Materials starting values': 'What a NEW form starts with. The person may still change it, and nothing already saved changes.',
}

export function SystemSettingsPage() {
  const { profile } = useAuth()
  const isAdmin = profile?.role === 'admin'

  const { data: rows, loading, refresh } = useQuery<any>('system_settings_row',
    () => supabase.from('company_profile').select('*').eq('id', 1), [], { cacheable: false })
  const { data: history, loading: hl, refresh: refreshHistory } = useQuery<any>('system_settings_history',
    () => supabase.from('document_audit')
      .select('id, doc_id, from_status, to_status, actor_email, comments, created_at')
      .eq('doc_type', 'SETTING').order('created_at', { ascending: false }).limit(200),
    [], { cacheable: false })
  const people = usePeople()

  const row = rows[0]
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [asking, setAsking] = useState<SettingDef | null>(null)

  // What the boxes hold: the saved value until somebody types.
  useEffect(() => {
    if (!row) return
    setDraft(Object.fromEntries(COMPANY_SETTINGS.map((d) => [d.key, String(row[d.key] ?? '')])))
  }, [row])

  if (!isAdmin) {
    return <NotPermitted what="change the system settings" who="the System Administrator" />
  }

  const groups: Array<SettingDef['group']> = ['Company-wide', 'Materials', 'Materials warnings', 'Materials starting values']

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-slate-900">
            <SlidersHorizontal className="h-5 w-5 text-oval-700" /> System Settings
          </h1>
          <p className="text-sm text-slate-500">
            The business rules the whole company runs on. Only the System Administrator may change
            them, every change needs a reason, and every change is kept below.
          </p>
        </div>
        <Link to="/service/settings" className="btn-secondary btn-sm">
          Service settings <ArrowRight className="ml-1 h-4 w-4" />
        </Link>
      </div>

      <Alert tone="info" title="A change applies immediately, everywhere">
        The screens, the database checks and every device pick up the new value at once — a site
        tablet on its next sign-in or refresh. Change one thing at a time, and say why.
      </Alert>

      {loading && !row && <Loading label="Reading the settings…" />}

      {/* A setting shows only once its column exists, so the page is safe
          to publish before or after the database change. */}
      {row && groups.filter((g) => COMPANY_SETTINGS.some((d) => d.group === g && d.key in row)).map((g) => (
        <Card key={g} title={g} subtitle={GROUP_SUBTITLE[g]}>
          <div className="divide-y divide-slate-100">
            {COMPANY_SETTINGS.filter((d) => d.group === g && d.key in row).map((d) => {
              const saved = String(row[d.key] ?? '')
              const val = draft[d.key] ?? saved
              const changed = val !== saved
              return (
                <div key={d.key} className="grid gap-3 py-3 lg:grid-cols-[1fr_280px_auto] lg:items-center">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{d.label}</p>
                    <p className="mt-0.5 text-xs text-slate-500">{d.help}</p>
                    <p className="mt-1 text-[11px] text-slate-400">
                      Now: <strong className="text-slate-600">{show(d, row[d.key])}</strong>
                      {d.kind !== 'bool' && <> · allowed {d.min?.toLocaleString('en-IN')}–{d.max?.toLocaleString('en-IN')}{d.unit === '%' ? '%' : d.unit === 'th' || !d.unit ? '' : ` ${d.unit}`}</>}
                    </p>
                  </div>
                  <div>
                    {d.kind === 'bool' ? (
                      <Select value={val} onChange={(e) => setDraft({ ...draft, [d.key]: e.target.value })}
                              options={[
                                { value: 'true', label: d.onLabel ?? 'Yes' },
                                { value: 'false', label: d.offLabel ?? 'No' },
                              ]} />
                    ) : (
                      <Input type="number" step={d.kind === 'pct' ? '0.01' : d.kind === 'qty' ? '0.001' : '1'} min={d.min} max={d.max}
                             value={val} onChange={(e) => setDraft({ ...draft, [d.key]: e.target.value })} />
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button className="btn-primary btn-sm" disabled={!changed} onClick={() => setAsking(d)}>
                      <Save className="mr-1 h-4 w-4" /> Save
                    </button>
                    {changed && (
                      <button className="btn-ghost btn-sm" onClick={() => setDraft({ ...draft, [d.key]: saved })}>
                        Undo
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </Card>
      ))}

      <Card title="Change history" subtitle="Every change to these settings, newest first — kept permanently"
            actions={<History className="h-4 w-4 text-slate-400" />}>
        {hl && <Loading />}
        {!hl && history.length === 0 && (
          <EmptyState title="No changes yet"
                      message="Every setting is still at the value it started with." />
        )}
        {history.length > 0 && (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr><th>When</th><th>Setting</th><th>From</th><th>To</th><th>By</th><th>Why</th></tr>
              </thead>
              <tbody>
                {history.map((h: any) => (
                  <tr key={h.id}>
                    <td className="whitespace-nowrap text-xs">{dateTime(h.created_at)}</td>
                    <td className="text-sm">{LABEL[h.doc_id] ?? h.doc_id}</td>
                    <td className="text-xs text-slate-500">{h.from_status ?? '—'}</td>
                    <td className="text-xs font-semibold">{h.to_status ?? '—'}</td>
                    <td className="text-xs">{h.actor_email ? people.name({ email: h.actor_email }) : 'System'}</td>
                    <td className="max-w-[360px] text-xs text-slate-600">{h.comments}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {asking && (
        <ReasonModal def={asking} from={show(asking, row?.[asking.key])}
                     to={show(asking, draft[asking.key])} value={draft[asking.key]}
                     onClose={() => setAsking(null)}
                     onSaved={() => { setAsking(null); refresh(); refreshHistory() }} />
      )}
    </div>
  )
}

function ReasonModal({ def, from, to, value, onClose, onSaved }: {
  def: SettingDef; from: string; to: string; value: string
  onClose: () => void; onSaved: () => void
}) {
  const toast = useToast()
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const short = useMemo(() => reason.trim().length < 10, [reason])

  async function save() {
    setBusy(true)
    const { error } = await supabase.rpc('rpc_set_company_setting', {
      p_key: def.key, p_value: String(value).trim(), p_reason: reason.trim(),
    })
    setBusy(false)
    if (error) { toast.error(error.message); return }
    toast.success(`${def.label}: now ${to}. It applies immediately, everywhere.`)
    onSaved()
  }

  return (
    <Modal open onClose={onClose} title="Change a system setting" subtitle={def.label}
           footer={<>
             <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
             <button className="btn-primary" onClick={() => void save()} disabled={busy || short}>
               {busy ? 'Saving…' : 'Save the change'}
             </button>
           </>}>
      <p className="text-sm">
        From <strong>{from}</strong> to <strong>{to}</strong>.
      </p>
      <div className="mt-3">
        <Field label="Why is it changing?" required
               hint="At least a short sentence. Kept permanently in the change history — name who decided it.">
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Board decision 30-09-2026: timber and aggregates arrive up to 12% over the order." />
        </Field>
      </div>
    </Modal>
  )
}
